/**
 * @module LanguageContext
 * @description Provides multilingual support (EN/DE/FR) across the application.
 * Default language is German (de) — the primary target market.
 * Exposes current language code, setter, and resolved translation object via React Context.
 */
import { createContext, useContext, useState, useEffect } from 'react';
import translations from './translations';

const LanguageContext = createContext();

/**
 * Wraps the app to provide language state and translations to all children.
 * Updates the HTML lang attribute for accessibility and SEO on every language change.
 * @param {{ children: React.ReactNode }} props
 */
export const LanguageProvider = ({ children }) => {
  const [lang, setLang] = useState('de');
  const t = translations[lang];

  useEffect(() => {
    document.documentElement.lang = lang;
  }, [lang]);

  return (
    <LanguageContext.Provider value={{ lang, setLang, t }}>
      {children}
    </LanguageContext.Provider>
  );
};

/** Hook to access { lang, setLang, t } from the nearest LanguageProvider. */
export const useLanguage = () => useContext(LanguageContext);
